import React, { useEffect, useState } from "react";
import { collection, doc, getDoc, onSnapshot, query, setDoc, where, limit } from "firebase/firestore";
import { toast } from "react-hot-toast";
import { db } from "../../lib/firebase";
import { call, callableMessage } from "../../services/callables";
import { Button } from "../../components/common/Button";

/** Admin: direct UPI (no PayU) settings + pending UTR approvals. */
export const DirectUpiAdminPanel: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [vpa, setVpa] = useState("");
  const [payeeName, setPayeeName] = useState("");
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    getDoc(doc(db, "settings", "global")).then((s) => {
      const d = s.data()?.directUpi || {};
      setEnabled(d.enabled === true); setVpa(d.vpa || ""); setPayeeName(d.payeeName || "");
    }).catch(() => {});
    const q = query(collection(db, "direct_upi_requests"), where("status", "==", "pending"), limit(100));
    return onSnapshot(q, (s) => setRows(s.docs.map((d) => ({ id: d.id, ...(d.data() as any) })).sort((a, b) => (b.submittedAt?.toMillis?.() || 0) - (a.submittedAt?.toMillis?.() || 0))), (e) => console.error(e));
  }, []);

  const save = async () => {
    const v = vpa.trim();
    if (enabled && (!/^[A-Za-z0-9._-]{2,256}@[A-Za-z][A-Za-z0-9.-]{1,63}$/.test(v) || !payeeName.trim())) {
      return toast.error("Enter a valid UPI ID (name@bank) and payee name");
    }
    setSaving(true);
    try {
      await setDoc(doc(db, "settings", "global"), { directUpi: { enabled, vpa: v, payeeName: payeeName.trim().slice(0, 50) } }, { merge: true });
      toast.success("Saved");
    } catch (e: any) { toast.error(e?.message || "Save failed"); } finally { setSaving(false); }
  };

  const act = async (ref: string, op: "approve" | "reject") => {
    if (op === "approve" && !window.confirm(`Approve ${ref}? Only if the money is in your bank account.`)) return;
    const note = op === "reject" ? window.prompt("Reason (optional)") || "" : "";
    setBusy(ref);
    try {
      const r: any = await call("adminDirectUpiOp", { ref, op, note });
      toast.success(r?.already ? `Already ${r.status}` : `${ref} ${r.status}`);
    } catch (e: any) { toast.error(callableMessage(e, "Failed")); } finally { setBusy(null); }
  };

  return (
    <div className="mt-6 border rounded-xl p-4 space-y-4">
      <h3 className="font-bold text-gray-800">Direct UPI (backup, no PayU)</h3>
      <div className="grid sm:grid-cols-3 gap-2 items-center">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enabled</label>
        <input className="border rounded px-2 py-1 text-sm" placeholder="UPI ID e.g. ruchira@okicici" value={vpa} onChange={(e) => setVpa(e.target.value)} />
        <input className="border rounded px-2 py-1 text-sm" placeholder="Payee name" value={payeeName} onChange={(e) => setPayeeName(e.target.value)} />
      </div>
      <Button onClick={save} isLoading={saving} className="text-sm">Save UPI settings</Button>
      <div>
        <h4 className="font-semibold text-sm mb-2">Waiting for approval ({rows.length})</h4>
        {rows.length === 0 && <p className="text-xs text-gray-500">Nothing pending.</p>}
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-t py-2 text-sm">
            <div>
              <b>₹{Number(r.amount).toFixed(2)}</b> · ref {r.id} · UTR <b>{r.utr}</b>
              <div className="text-xs text-gray-500">user {r.uid} · {r.submittedAt?.toDate?.().toLocaleString?.() || ""}</div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => act(r.id, "approve")} disabled={busy === r.id} className="text-xs bg-green-600">Approve</Button>
              <Button onClick={() => act(r.id, "reject")} disabled={busy === r.id} className="text-xs bg-red-600">Reject</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
